import moment from "moment";
import isEmpty from "lodash/isEmpty";

// API
import { fetchStudentDetails } from "@/api";

// Actions
import { setShowStudentDetailsInMobile, showToasterMessage } from "./commonActions";
import { registeredEventsGetAndStore } from "./registeredEventsActions";
import { goToRoute } from "./navigationActions";
import { RESET_STUDENT_NOTES, studentNotesGetAndStore } from "./notesActions";

// Constants
export const LOADING_STUDENTS = "LOADING_STUDENTS";
export const INITIALIZE_STUDENT_DETAILS = "INITIALIZE_STUDENT_DETAILS";
export const LOADING_INDIVIDUAL_STUDENT_DETAILS = "LOADING_INDIVIDUAL_STUDENT_DETAILS";
export const INITIALIZE_PARTICIPATING_UNIVERSITY_DETAILS = "INITIALIZE_PARTICIPATING_UNIVERSITY_DETAILS";
export const LOCAL_DATA_APPEND_KEY = "connect_student_list";
import { toasterMessage } from "@/utils/toaster-msg";

// Utils
import { storeInSession, deleteFromSession, retriveFromSession } from "@/utils/session";
import { reorderParticipatingUniversities } from "@/utils/students";
import { deserializeStudentDetails } from "@/utils/deserializers/studentDetailsDeserializer";
import { getLocalData, removeLocalData, setLocalData } from "@/utils/localStorage";
import routePaths from "@/utils/route-paths";

// api
import { setUniversityAsMustVisit } from "@/api/university";
import { upcomingEventsGetAndStore } from "./upcomingEventsActions";
import { meetingsGetAndStore, meetingsOfCounsellor } from "./meetingsActions";

export const initializeStudentDetails = (data, oldData) => {
    return {
        type: INITIALIZE_STUDENT_DETAILS,
        payload: {
            data: data || [],
            oldData: oldData || [],
        },
    };
};
export const loadingStudents = (payload) => {
    return {
        type: LOADING_STUDENTS,
        payload,
    };
};
export const loadingIndividualStudentDetails = (payload) => {
    return {
        type: LOADING_INDIVIDUAL_STUDENT_DETAILS,
        payload,
    };
};

export const initializeParticipatingUniversitiesDetails = (payload) => {
    return {
        type: INITIALIZE_PARTICIPATING_UNIVERSITY_DETAILS,
        payload,
    };
};

export const updateMustVisitUniversity = (participatingUniversities, payload, setIsLoading) => {
    return (dispatch) => {
        setUniversityAsMustVisit(payload)
            .then((res) => {
                dispatch(
                    initializeParticipatingUniversitiesDetails({
                        registrantCode: payload.registrant_code,
                        participatingUniversities: reorderParticipatingUniversities(
                            participatingUniversities,
                            res.data.participating_universities
                        ),
                    })
                );
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false, err);
            });
    };
};

const getStudentsData = (data, universityCode, fairId) => {
    const localDataKey = `${LOCAL_DATA_APPEND_KEY}_${universityCode}_${fairId}`,
        localData = getLocalData(localDataKey),
        studentById = data.reduce((data, item, index) => {
            data[item.user_id] = index + 1;
            return data;
        }, {});

    const newLocalData = [...data];
    const oldData = [];

    if (!isEmpty(localData)) {
        if (moment().format("DD_MM_YYYY") === localData.date) {
            localData.data.forEach((item) => {
                if (!studentById[item.user_id]) {
                    oldData.push(item);
                    newLocalData.push(item);
                }
            });
        } else {
            removeLocalData(localData);
        }
    }

    setLocalData(localDataKey, { date: moment().format("DD_MM_YYYY"), data: newLocalData });
    return oldData;
};

/**
 * After a successful breakout move, remove only that student's Unique Id from search.
 * Other searched IDs stay in the chip list / URL.
 */
export const clearStudentSearchAfterSuccessfulMove = (movedRegistrantCode) => {
    return (dispatch, getState) => {
        const moved = `${movedRegistrantCode || ""}`.trim();
        const state = getState();
        const fromUrl = new URLSearchParams(state.router.location.search).get(
            "registrant_code"
        );
        const fromSession = retriveFromSession("registrantCode");
        const current = `${fromUrl || fromSession || ""}`
            .split(",")
            .map((code) => code.trim())
            .filter(Boolean);
        const remaining = current.filter((code) => code !== moved);

        dispatch({ type: RESET_STUDENT_NOTES });

        if (!remaining.length) {
            deleteFromSession("registrantCode");
            dispatch(initializeStudentDetails([]));
            dispatch(setShowStudentDetailsInMobile(false));
            dispatch(goToRoute(routePaths.DASHBOARD));
            return;
        }

        const remainingCodes = remaining.join(",");
        storeInSession("registrantCode", remainingCodes);
        const students = state.studentDetails.data || [];
        const oldData = state.studentDetails.oldData || [];
        dispatch(
            initializeStudentDetails(
                students.filter((s) => remaining.includes(`${s.registrantCode}`)),
                oldData.filter((s) => remaining.includes(`${s.registrantCode}`))
            )
        );
        // StudentSearch syncs chips from URL; keeps other IDs
        dispatch(
            goToRoute(routePaths.DASHBOARD, {}, {
                registrant_code: remainingCodes,
            })
        );
    };
};

export const getStudentDetails = (payload) => {
    return (dispatch) => {
        dispatch(loadingStudents(true));
        fetchStudentDetails(payload)
            .then((res) => {
                const oldData = getStudentsData(
                    res.data.student_details,
                    payload.university_code,
                    payload.fair_id
                );
                storeInSession("registrantCode", payload.registrant_code);
                dispatch(
                    initializeStudentDetails(
                        res.data.student_details.map(deserializeStudentDetails),
                        oldData.map(deserializeStudentDetails)
                    )
                );
                dispatch(setShowStudentDetailsInMobile(true));

                if (!isEmpty(res.data.errors)) {
                    const codes = res.data.errors.map((err) => {
                        const idx = err.message.indexOf(":");
                        return err.message.slice(0, idx);
                    });
                    dispatch(
                        showToasterMessage(
                            `Unique id(s) not found: ${codes.join(", ")}`,
                            toasterMessage.incorrectUniqueId.color
                        )
                    );
                }
            })
            .catch((err) => {
                console.log(err);
                dispatch(initializeStudentDetails([]));
                dispatch(setShowStudentDetailsInMobile(false));
            });
    };
};

export const getData = (userId, registrantCode, universityCode, fairID, userData) => {
    return (dispatch) => {
        dispatch(loadingIndividualStudentDetails(true));
        dispatch(
            studentNotesGetAndStore(registrantCode, { university_code: universityCode, fair_id: fairID })
        );
        dispatch(meetingsGetAndStore({ user_id: userId, statuses: "SCHEDULED" }));
        dispatch(meetingsOfCounsellor({ counsellor_id: userData.counsellorId, statuses: "SCHEDULED" }));
        dispatch(registeredEventsGetAndStore({ user_id: userId }));
        dispatch(upcomingEventsGetAndStore(userData));
    };
};
